import React, { useState, useRef, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import { COOKIES } from '../../_shared/constants/cookies';
import { createCookie } from '../../_shared/utils/cookies';
import BaseModal from '../../_shared/components/BaseModal';
import useTradeSearchList from '../../_shared/hooks/search/useTradeSearchList';
import OverlayTrigger from '../OverlayTrigger';
import TradeGrid from '../TradeGrid';
import {
  ListVariant,
  TR5TradeStates,
  TradeStateNew,
  TradeStateNewName
} from '../../_shared/types/trade';
import ROUTES from '../Routes/constants';
import {
  MAX_RECENT_SEARCH,
  RECENT_SEARCH_KEY,
  SEARCH_IN,
  SEARCH_KEY
} from '../../_shared/constants/search';
import ReducerConstants from '../../_shared/constants/reducers';
import theme from '../../_shared/themes';
import SearchNav from './SearchNav';
import Pagination from '../Pagination';
import renderLocale from '../../_shared/helpers/renderLocale';
import useEscModalTracking from '../../_shared/hooks/useEscModalTracking';
import useSearchTracking from '../../_shared/hooks/search/useSearchTracking';
import { localStorageService } from '../../services/StorageService';
import LOCALSTORAGE from '../../_shared/constants/localStorage';
import {
  ModalOuterBox,
  ModalCloseButton,
  ModalInnerBox,
  ModalSearchTitleWrapper,
  ModalSearchTitle,
  ModalDropdownIconWrapper,
  ModalDropdownIcon,
  ModalHintWrapper,
  KeyIcon,
  ModalOverlayWrapper,
  SearchResultWrapper,
  StyledHeading,
  FooterLabel
} from './Styles';
import SearchBar from './SearchBar';

const propTypes = {
  isModalOpen: PropTypes.bool,
  closeHandler: PropTypes.func.isRequired,
  searchSource: PropTypes.oneOf([SEARCH_IN.SELL, SEARCH_IN.BUY]),
  searchInputString: PropTypes.string,
  preSelectedTradeState: PropTypes.string,
  pageNumber: PropTypes.number,
  source: PropTypes.string,
  isOmp: PropTypes.bool
};

const defaultProps = {
  isModalOpen: false,
  searchSource: SEARCH_IN.BUY,
  searchInputString: undefined,
  preSelectedTradeState: undefined,
  pageNumber: 1,
  source: undefined,
  isOmp: false
};

const useEscListener = (isModalOpen, callback) => {
  useEffect(() => {
    const escHandler = event => {
      if (event.keyCode === SEARCH_KEY.ESC) {
        callback();
      }
    };
    if (isModalOpen) {
      document.addEventListener('keydown', escHandler);
    } else {
      document.removeEventListener('keydown', escHandler);
    }
    return () => document.removeEventListener('keydown', escHandler);
  }, [isModalOpen, callback]);
};
const SearchModal = ({
  testId,
  isModalOpen,
  closeHandler,
  searchSource,
  searchInputString,
  preSelectedTradeState,
  pageNumber,
  source,
  isOmp
}) => {
  const intl = useIntl();
  const history = useHistory();
  const searchResultWrapperRef = useRef(null);
  const searchPage = useRef(pageNumber);

  const [searchIn, setSearchIn] = useState(searchSource);
  const [showingResults, setShowingResults] = useState(false);
  const [isSearchFromRecentSearches, setIsSearchFromRecentSearches] = useState(
    false
  );
  const [recentSearches, setRecentSearches] = useState([]);
  const [tradeState, setTradeState] = useState(
    preSelectedTradeState || TradeStateNew.ALL
  );
  const [searchResults, setSearchResults] = useState([]);
  const [notExactMatchSearchResults, setNotExactMatchSearchResults] = useState(
    []
  );
  const [tradeCount, setTradeCount] = useState(0);
  const [searchResultFetched, setSearchResultFetched] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(true);
  const scrollToTradeId =
    localStorageService.getItem(LOCALSTORAGE.SEARCH_TRADE) || undefined;
  const auth = useSelector(state => state[ReducerConstants.AUTH], shallowEqual);
  const locale = renderLocale(auth);
  const canSell = auth?.user?.isCanSell;
  let closeSearchSourceOverlayCallback;
  let closeRecentSearchOverlayCallback;
  const searchInputRef = useRef(null);
  useEscModalTracking('Search', isModalOpen);
  useSearchTracking(
    isSearchFromRecentSearches,
    searchIn,
    searchResults,
    searchResultFetched,
    searchInputRef?.current?.value,
    tradeState,
    setIsSearchFromRecentSearches,
    setSearchResultFetched
  );
  useEscListener(isModalOpen, closeHandler);
  const getContextString = () => {
    return searchIn === SEARCH_IN.SELL ? (
      <FormattedMessage id="list.nav.searchbar.modal.search.selling" />
    ) : (
      <FormattedMessage id="list.nav.searchbar.modal.search.buying" />
    );
  };

  const historyPush = (ts, opt, pageN) => {
    if (isOmp) {
      return;
    }
    const searchPath = pageN
      ? `?q=${searchInputRef.current.value}&tradeState=${ts}&page=${pageN}`
      : `?q=${searchInputRef.current.value}&tradeState=${ts}`;
    history.push({
      pathname: `${ROUTES.SEARCH_URL}/${opt}`,
      search: searchPath
    });
  };
  const searchSourceChangeHandler = option => {
    // Handle for when user is on Lost/Parked but changes the search in option to buying
    if (
      option === SEARCH_IN.BUY &&
      (tradeState === TradeStateNew.LOST || tradeState === TradeStateNew.PARKED)
    ) {
      historyPush(TradeStateNew.ALL, option, undefined);
      setTradeState(TradeStateNew.ALL);
    } else {
      historyPush(tradeState, option, undefined);
    }
    setSearchIn(option);
    closeSearchSourceOverlayCallback();
  };
  const {
    data,
    hasPreviousPage,
    hasNextPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingPreviousPage,
    isLoading,
    isFetching,
    isFetchingNextPage
  } = useTradeSearchList(
    searchIn === SEARCH_IN.BUY,
    searchInputRef && searchInputRef.current
      ? searchInputRef.current.value
      : '',
    tradeState,
    isOmp ? searchPage.current - 1 : pageNumber - 1
  );
  const readRecentSearches = () => {
    const recentSearchesString = window.localStorage.getItem(RECENT_SEARCH_KEY);
    const recentSearchesArray = JSON.parse(recentSearchesString) || [];
    setRecentSearches(recentSearchesArray);
    return recentSearchesArray;
  };
  const writeRecentSearches = newValue => {
    const recentSearchesArray = readRecentSearches();
    const indexOfNewVal =
      recentSearchesArray && recentSearchesArray.indexOf(newValue);
    // If not present in list of searches
    if (indexOfNewVal === -1) {
      // Max entry limit reached
      if (recentSearchesArray.length >= MAX_RECENT_SEARCH) {
        recentSearchesArray.pop();
      }
      recentSearchesArray.unshift(newValue);
      window.localStorage.setItem(
        RECENT_SEARCH_KEY,
        JSON.stringify(recentSearchesArray)
      );
      setRecentSearches(recentSearchesArray);
    } else {
      // If present in list of searches
      let beforeVal = recentSearchesArray.slice(0, indexOfNewVal);
      const afterVal = recentSearchesArray.slice(
        indexOfNewVal + 1,
        recentSearchesArray.length
      );
      beforeVal = beforeVal.concat(afterVal);
      beforeVal.unshift(newValue);
      window.localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(beforeVal));
      setRecentSearches(beforeVal);
    }
  };

  const handleSearch = pageN => {
    setShowingResults(true);
    if (typeof closeRecentSearchOverlayCallback === 'function') {
      closeRecentSearchOverlayCallback();
    }
    writeRecentSearches(searchInputRef.current.value);
    historyPush(tradeState, searchIn, pageN);
  };
  const recentSearchClickHandler = value => {
    searchInputRef.current.value = value;
    handleSearch();
  };
  const handleAfterModalOpen = () => {
    searchInputRef.current.focus();
    // Track where the user landed on when search page is opened for the first time
    if (
      !isOmp &&
      history.location &&
      !history.location.pathname.startsWith(ROUTES.SEARCH_URL)
    ) {
      createCookie(COOKIES.GO_BACK_FROM_SEARCH, window.location.href);
    }
    if (searchInputString !== undefined && searchInputString !== '') {
      searchInputRef.current.value = searchInputString;
      handleSearch(pageNumber);
    } else {
      setShowingResults(false);
    }
    readRecentSearches();
  };

  const handleTradeStateSelection = tradeStateSelected => {
    setTradeState(tradeStateSelected);
    historyPush(tradeStateSelected, searchIn, undefined);
  };
  const tradeSelectionHandler = (tradeId, tradeStateName) => {
    localStorageService.setItem(LOCALSTORAGE.SEARCH_TRADE, tradeId);
    if (
      searchIn === SEARCH_IN.BUY &&
      tradeStateName &&
      TR5TradeStates.includes(tradeStateName)
    ) {
      history.push(`${ROUTES.TRADE_URI}${tradeId}`);
      window.scrollTo(0, 0);
    } else if (isOmp) {
      window.location.href = `${ROUTES.TRADE_URI}${tradeId}`;
    } else {
      window.location.href = `${ROUTES.TR4_ENDPOINT}trade/${tradeId}`;
    }
  };
  const getNextPage = () => {
    fetchNextPage();
    const page = Number(pageNumber) + 1;
    searchPage.current += 1;
    historyPush(tradeState, searchIn, page);
  };
  const getPrevPage = () => {
    fetchPreviousPage();
    const page = Number(pageNumber) - 1;
    searchPage.current -= 1;
    historyPush(tradeState, searchIn, page);
  };
  const isNotLoadingResults = () =>
    !isFetchingNextPage &&
    !isFetchingPreviousPage &&
    !isLoading &&
    !isFetching &&
    !resultsLoading;
  useEffect(() => {
    if (data?.pages && data?.pages[data?.pages?.length - 1]) {
      if (isFetchingPreviousPage) {
        setSearchResults(data.pages[0]?.exactMatchTrades);
        setNotExactMatchSearchResults(data.pages[0]?.notExactMatchTrades);
        setTradeCount(data.pages[0]?.tradeCount);
      } else {
        setSearchResults(data.pages[data?.pages?.length - 1]?.exactMatchTrades);
        setNotExactMatchSearchResults(
          data.pages[data?.pages?.length - 1]?.notExactMatchTrades
        );
        setTradeCount(data.pages[data?.pages?.length - 1]?.tradeCount);
      }
      setSearchResultFetched(true);
      setResultsLoading(false);
    }
  }, [data?.pages, isFetchingPreviousPage]);
  useEffect(() => {
    setTimeout(() => {
      if (
        scrollToTradeId &&
        !isFetchingNextPage &&
        !isFetchingPreviousPage &&
        !isLoading &&
        !isFetching &&
        !resultsLoading
      ) {
        const tradeEle = document.getElementById(
          `tradeCard-${scrollToTradeId}`
        );
        if (tradeEle) {
          tradeEle.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }
        localStorageService.removeItem(LOCALSTORAGE.SEARCH_TRADE);
      }
    }, 1000);
  }, [
    scrollToTradeId,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isLoading,
    isFetching,
    resultsLoading
  ]);

  useEffect(() => {
    if (isOmp) return undefined;
    return history.listen(() => {
      if (history.action === 'POP') {
        // Back event
        closeHandler();
      }
    });
  }, [closeHandler, history, isOmp]);

  let modalVariant = 'none';
  if (isOmp) {
    modalVariant = 'ompSearchOverlay';
  } else if (!showingResults) {
    modalVariant = 'trFullScreenSearchBar';
  }

  useEffect(() => {
    searchPage.current = 1;
  }, [tradeState]);

  return (
    <BaseModal
      isOpen={isModalOpen}
      isFullScreen={!isOmp}
      variant={modalVariant}
      padding="0"
      bodyOpenClassName="ReactModal__FullScreen"
      isClickEscClose
      onAfterOpen={handleAfterModalOpen}
      onRequestClose={closeHandler}
      backgroundColor={
        showingResults || source === 'search'
          ? theme.COLORS.NEUTRAL_60
          : theme.COLORS.TRANSPARENT
      }
    >
      <ModalOuterBox isOmp={isOmp} showingResults={showingResults}>
        <ModalCloseButton
          data-testid={`${testId}-searchModal-closeButton`}
          onClick={closeHandler}
          isOmp={isOmp}
        >
          {!isOmp && (
            <FormattedMessage id="list.nav.searchbar.modal.closeButton" />
          )}
          <ModalCloseButton.Icon isOmp={isOmp} />
        </ModalCloseButton>
        <ModalInnerBox isOmp={isOmp} showingResults={showingResults}>
          <ModalSearchTitleWrapper>
            <ModalSearchTitle>
              <FormattedMessage
                id={
                  isOmp
                    ? 'list.nav.searchbar.modal.searchbar.title.omp.sell'
                    : 'list.nav.searchbar.modal.searchbar.title'
                }
              />
            </ModalSearchTitle>
            <OverlayTrigger
              onRef={callback => {
                closeSearchSourceOverlayCallback = callback;
              }}
              placement="right"
              overlay={
                <ModalOverlayWrapper>
                  {canSell && (
                    <ul>
                      <li
                        role="presentation"
                        onClick={() =>
                          searchSourceChangeHandler(SEARCH_IN.SELL)
                        }
                      >
                        <FormattedMessage id="list.nav.searchbar.modal.search.selling" />
                      </li>
                      <li
                        role="presentation"
                        onClick={() => searchSourceChangeHandler(SEARCH_IN.BUY)}
                      >
                        <FormattedMessage id="list.nav.searchbar.modal.search.buying" />
                      </li>
                    </ul>
                  )}
                </ModalOverlayWrapper>
              }
            >
              <ModalSearchTitle underlined>
                {isOmp ? null : getContextString()}
                {canSell && !isOmp && (
                  <ModalDropdownIconWrapper>
                    <ModalDropdownIcon />
                  </ModalDropdownIconWrapper>
                )}
              </ModalSearchTitle>
            </OverlayTrigger>
          </ModalSearchTitleWrapper>
          <SearchBar
            testId={testId}
            applyPadding={isOmp && !showingResults}
            searchInputRef={searchInputRef}
            handleSearch={handleSearch}
            enableHistoryOverlay
            onHistoryItemClick={recentSearch => {
              setIsSearchFromRecentSearches(true);
              recentSearchClickHandler(recentSearch);
            }}
            recentSearches={recentSearches}
            isOmp={isOmp}
            showingResults={showingResults}
          />
          {showingResults && (
            <SearchNav
              onTradeStateClick={handleTradeStateSelection}
              preSelectedTradeState={preSelectedTradeState}
              searchIn={searchIn}
              data-testid={`${testId}-searchModal-searchNav`}
              locale={locale}
              isOmp={isOmp}
            />
          )}
        </ModalInnerBox>
        {!showingResults && (
          <ModalHintWrapper>
            <FormattedMessage
              id="list.nav.searchbar.modal.hint.sKey"
              values={{
                sKey: chunks => <KeyIcon>{chunks}</KeyIcon>
              }}
            />
            <FormattedMessage
              id="list.nav.searchbar.modal.hint.escKey"
              values={{
                escKey: chunks => <KeyIcon esc>{chunks}</KeyIcon>
              }}
            />
          </ModalHintWrapper>
        )}
      </ModalOuterBox>
      {showingResults && (
        <SearchResultWrapper isOmp={isOmp} ref={searchResultWrapperRef}>
          {isNotLoadingResults() ? (
            <StyledHeading>
              <FormattedMessage
                id="list.nav.searchbar.modal.search.results"
                values={{
                  resultsMatching: tradeCount,
                  inputString: searchInputRef?.current?.value,
                  state:
                    tradeState !== TradeStateNew.ALL ? (
                      intl.formatMessage({
                        id: `tradeState.${TradeStateNewName[
                          tradeState
                        ].toLowerCase()}`
                      })
                    ) : (
                      <FormattedMessage id="list.results.total" />
                    ),
                  context: getContextString()
                }}
              />
            </StyledHeading>
          ) : (
            <StyledHeading>
              <FormattedMessage
                id="list.nav.searchbar.modal.search.results.loading"
                values={{
                  inputString: searchInputRef?.current?.value,
                  context:
                    tradeState !== TradeStateNew.ALL
                      ? TradeStateNewName[tradeState]
                      : getContextString()
                }}
              />
            </StyledHeading>
          )}
          {hasPreviousPage &&
            searchResults?.length > 0 &&
            isNotLoadingResults() && (
              <Pagination
                loadPrevPage={getPrevPage}
                loadNextPage={getNextPage}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
                pageNumber={isOmp ? searchPage.current : pageNumber}
                isOmp={isOmp}
              />
            )}
          <TradeGrid
            testId="tradeSearchGrid"
            tradeSummaries={searchResults}
            variant={ListVariant.SEARCH}
            tradeSelectionHandler={tradeSelectionHandler}
            tradeState={getContextString()}
            isLoading={!isNotLoadingResults()}
            searchIn={searchIn}
            isOmp={isOmp}
          />
          {(hasNextPage || hasPreviousPage) &&
            searchResults?.length > 0 &&
            isNotLoadingResults() && (
              <Pagination
                loadPrevPage={getPrevPage}
                loadNextPage={getNextPage}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
                pageNumber={isOmp ? searchPage.current : pageNumber}
                isOmp={isOmp}
              />
            )}
          {searchResults?.length > 0 && isNotLoadingResults() && !hasNextPage && (
            <FooterLabel>
              <FormattedMessage id="list.nav.searchbar.modal.search.end" />
            </FooterLabel>
          )}
          {notExactMatchSearchResults?.length > 0 &&
            searchIn === SEARCH_IN.BUY &&
            (tradeState === TradeStateNew.ALL ||
              Number(tradeState) === TradeStateNew.UPCOMING ||
              Number(tradeState) === TradeStateNew.ACTIVE) &&
            isNotLoadingResults() && (
              <>
                <StyledHeading>
                  <FormattedMessage id="list.nav.searchbar.modal.similar.trades" />
                </StyledHeading>
                <TradeGrid
                  testId="tradeSearchGridNotExactMatch"
                  tradeSummaries={notExactMatchSearchResults}
                  variant={ListVariant.SEARCH}
                  tradeSelectionHandler={tradeSelectionHandler}
                />
              </>
            )}
        </SearchResultWrapper>
      )}
    </BaseModal>
  );
};
SearchModal.propTypes = propTypes;
SearchModal.defaultProps = defaultProps;
export default SearchModal;
